import { useEffect, useState } from 'react'
import { api } from '../../api'
import { getCached, setCached } from '../../lib/swr'
import ConfirmStandSheet from './ConfirmStandSheet'
import CarteEvenementProducteur from './CarteEvenementProducteur'
import { ORANGE, estCanalMarche, estStandDefaut } from './marcheProducer'

/**
 * Marche producteur — onglet « Événements » (proto v7.2.1, mEvtsP).
 * Liste des evenements a venir : inscription + CONFIRMATION DATEE du stand
 * (feuille pre-cochee sur le stand de reference). Bandeau vert une fois
 * confirme ; tant que non confirme, le stand n'est pas publie cote client.
 *
 * Cache SWR (S29, E-13) : rouvrir le module reaffiche INSTANTANEMENT la
 * derniere liste connue, puis revalide en silence. Sans cela, chaque aller-retour
 * repassait par le squelette de chargement.
 */
const CLE_PARTICIPATIONS = 'producer/marches'
const CLE_DISPONIBLES = 'producer/available-marches'
const CLE_OFFRES = 'producer/offers'
const CLE_STAND_COUNTS = 'producer/stand-counts'

/**
 * @param {string|null} titre   intitule de section (null = aucun)
 * @param {boolean} enAvant     premiere carte en variante « prochain evenement »
 * @param {number} limite       nombre max de cartes (accueil) ; `onVoirTout` ajoute
 *                              un lien « Tous les evenements » s'il en reste
 */
function MarcheProducerEvents({ onToast, titre = 'Événements à venir', enAvant = false, limite, onVoirTout }) {
  const [participations, setParticipations] = useState(() => getCached(CLE_PARTICIPATIONS) || [])
  const [disponibles, setDisponibles] = useState(() => getCached(CLE_DISPONIBLES) || [])
  const [standCounts, setStandCounts] = useState(() => getCached(CLE_STAND_COUNTS) || {}) // participationId -> nb offres
  const [offres, setOffres] = useState(() => getCached(CLE_OFFRES) || [])
  const [loading, setLoading] = useState(() => getCached(CLE_PARTICIPATIONS) === undefined)
  const [sheet, setSheet] = useState(null) // { marche, participation|null, initialSelected }
  const [saving, setSaving] = useState(false)
  const [retraitEnCours, setRetraitEnCours] = useState(null) // participationId

  const charger = () => {
    Promise.all([api.producer.getMarches(), api.producer.getAvailableMarches(), api.producer.getOffers()])
      .then(async ([mes, dispo, offers]) => {
        const offresMarche = (offers || []).filter((o) => o.status === 'ACTIVE' && estCanalMarche(o))
        setParticipations(mes || [])
        setDisponibles(dispo || [])
        setOffres(offresMarche)
        setCached(CLE_PARTICIPATIONS, mes || [])
        setCached(CLE_DISPONIBLES, dispo || [])
        setCached(CLE_OFFRES, offresMarche)
        // Nb de produits du stand confirme (bandeau vert)
        const confirmees = (mes || []).filter((p) => p.standConfirmeAt)
        const counts = await Promise.all(
          confirmees.map((p) => api.producer.getStandOffers(p.id).then((so) => [p.id, (so || []).length]).catch(() => [p.id, 0])),
        )
        setStandCounts(Object.fromEntries(counts))
        setCached(CLE_STAND_COUNTS, Object.fromEntries(counts))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(charger, [])

  const ouvrirFeuille = async (marche, participation) => {
    let initial
    if (participation?.standConfirmeAt) {
      const so = await api.producer.getStandOffers(participation.id).catch(() => [])
      initial = (so || []).map((x) => x.productOfferId || x.productOffer?.id).filter(Boolean)
      if (initial.length === 0) initial = offres.filter(estStandDefaut).map((o) => o.id)
    } else {
      initial = offres.filter(estStandDefaut).map((o) => o.id)
    }
    setSheet({ marche, participation, initialSelected: initial })
  }

  const confirmer = async (ids) => {
    setSaving(true)
    try {
      let participation = sheet.participation
      if (!participation) participation = await api.producer.joinMarche(sheet.marche.id)
      await api.producer.confirmStand(participation.id, ids)
      onToast?.(`C'est noté — ${ids.length} produit${ids.length > 1 ? 's' : ''} visible${ids.length > 1 ? 's' : ''} ce jour-là ✓`)
      setSheet(null)
      charger()
    } catch (e) {
      onToast?.(e.message || 'La confirmation a échoué — réessaie.')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Se desinscrire (S60, remonte par Enzo : « une fois inscrit, aucune
   * possibilite de se desinscrire »).
   *
   * L'API l'autorise depuis la v0.2.0 et `EventCard` avait le bouton — mais ce
   * composant-la sert une AUTRE page. L'onglet que le producteur utilise
   * vraiment ne l'a jamais eu : le geste existait partout sauf la ou il fallait.
   *
   * On n'anticipe pas le refus cote client au-dela de la fenetre d'inscription :
   * le backend porte les autres regles (participation deja confirmee la veille
   * par le cron, evenement passe) et renvoie un message clair, qu'on affiche tel
   * quel plutot que de le deviner.
   */
  const seDesinscrire = async (marche, participation) => {
    const quoi = marche.nom || marche.lieu || 'cet évènement'
    if (!confirm(
      `Se désinscrire de ${quoi} ?\n\n`
      + 'Vos produits ne seront plus visibles pour les clients ce jour-là. '
      + 'Vous pourrez vous réinscrire tant que les inscriptions sont ouvertes.'
    )) return
    setRetraitEnCours(participation.id)
    try {
      await api.producer.withdrawParticipation(participation.id)
      onToast?.('Désinscription enregistrée.')
      charger()
    } catch (e) {
      onToast?.(e.message || 'La désinscription a échoué — réessaie.')
    } finally {
      setRetraitEnCours(null)
    }
  }

  // Fusion : mes participations (avec leur event) + marches disponibles (events
  // bruts), puis TRI CHRONOLOGIQUE. Sans ce tri, la simple concatenation
  // affichait un marche disponible proche APRES une participation lointaine
  // (S29) — le prototype liste toujours les evenements par date croissante.
  const cartes = [
    ...participations.map((p) => ({ marche: p.marche || p.event, participation: p })),
    ...disponibles.map((m) => ({ marche: m.marche || m, participation: null })),
  ]
    .filter((c) => c.marche)
    .sort((a, b) => new Date(a.marche.date) - new Date(b.marche.date))

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => <div key={i} className="h-32 rounded-3xl animate-pulse bg-ink/5" />)}
      </div>
    )
  }

  return (
    <>
      {titre && (
        <p className="text-[11px] font-bold uppercase mb-2.5 mx-0.5" style={{ letterSpacing: 2, color: ORANGE }}>
          {titre}
        </p>
      )}
      {cartes.length === 0 && (
        <div className="glass-surface rounded-3xl p-6 text-center">
          <span className="text-4xl block mb-2">🎪</span>
          <p className="text-[13px] text-ink/65">Aucun événement à venir pour le moment.</p>
        </div>
      )}
      <div className="flex flex-col gap-2.5">
        {(limite ? cartes.slice(0, limite) : cartes).map(({ marche, participation }, i) => (
          <CarteEvenementProducteur
            key={marche.id}
            marche={marche}
            participation={participation}
            nbProduits={standCounts[participation?.id]}
            enAvant={enAvant && i === 0}
            retraitEnCours={retraitEnCours}
            onOuvrirFeuille={ouvrirFeuille}
            onDesinscrire={seDesinscrire}
          />
        ))}
        {limite && cartes.length > limite && onVoirTout && (
          <button onClick={onVoirTout} className="text-[12.5px] font-bold text-center py-1" style={{ color: ORANGE }}>
            Tous les évènements ({cartes.length}) ›
          </button>
        )}
      </div>

      <ConfirmStandSheet
        open={!!sheet}
        marche={sheet?.marche}
        offres={offres}
        initialSelected={sheet?.initialSelected}
        dejaConfirme={!!sheet?.participation?.standConfirmeAt}
        saving={saving}
        onClose={() => setSheet(null)}
        onConfirm={confirmer}
      />
    </>
  )
}

export default MarcheProducerEvents
